import { useMemo } from 'react';
import { getAttributes } from '../utils';
import { getMobileOperatingSystem, useHandleAction } from './handle-action';
import { useContent, Content } from '../../brite-viewer-v2/ContentProvider';
import { ArrowSquareOut, BookOpen, Chat, EnvelopeSimple, Phone } from 'phosphor-react';
import { formatPhoneNumber } from '../../decision-tool-views/recommendation-utils';
import { QRComponent } from './QRComponent';
import { useStore } from '../../store-provider/use-store';
import AndroidIcon from './assets/android-logo-fill.svg';
import AppleIcon from './assets/apple-logo-fill.svg';

function getSmsUrl(sendToNumber, message, device = null) {
  const currentDevice = device || getMobileOperatingSystem();
  const body = encodeURIComponent(message);
  let url = `sms:${sendToNumber}`;
  if (currentDevice === 'android' && !!body) {
    return `${url}?body=${body}`;
  } else {
    return `${url}&body=${body}`;
  }
}

function getCallUrl(callNumber) {
  return `tel:${callNumber}`;
}

function getEmailUrl(data) {
  try {
    const body = encodeURIComponent(data.body);
    const subject = encodeURIComponent(data.subject);
    return `mailto:${data.sendToEmail}?subject=${subject}&body=${body}`;
  } catch {
    return '';
  }
}

export const Button = ({ item, totalColumns }) => {
  const content = useContent();
  const type = Content.getViewMode(content);
  const page = Content.getPage(content);

  const componentAttributes = getAttributes({
    column: item,
    length: totalColumns,
    type: 'component',
  });

  const handleAction = useHandleAction(item);
  switch (type) {
    case 'pdf': {
      return <PrintButton item={item} page={page} />;
    }
    default: {
      return (
        <button {...componentAttributes} onClick={handleAction}>
          {item.content}
        </button>
      );
    }
  }
};

const PrintButton = ({ page, item }) => {
  const store = useStore();

  const qrProps = useMemo(() => {
    if (!item?.action?.type) return null;

    switch (item.action.type) {
      case 'call-phone': {
        return {
          Icon: Phone,
          title: item?.content,
          description: formatPhoneNumber(item?.action?.data?.callNumber),
          links: [{ url: getCallUrl(item?.action?.data?.callNumber) }],
        };
      }
      case 'open-sms': {
        return {
          Icon: Chat,
          title: item?.content,
          description: formatPhoneNumber(item?.action?.data?.sendToNumber),
          links: [
            {
              url: getSmsUrl(item?.action?.data?.sendToNumber, item?.action?.data?.message, 'android'),
              centerImageSrc: AndroidIcon,
            },
            {
              url: getSmsUrl(item?.action?.data?.sendToNumber, item?.action?.data?.message, 'ios'),
              centerImageSrc: AppleIcon,
            },
          ],
        };
      }
      case 'route-to-page': {
        const url = new URL(window.location.href);
        const pageIndex = store?.data?.pages.findIndex(({ ID }) => ID === item?.action?.data?.pageId) ?? 0;
        const page = store?.data?.pages[pageIndex];
        const pageUrl = `${url.protocol}//${url.hostname}/${pageIndex + 1}${window.location.search}`;
        return {
          Icon: BookOpen,
          title: item?.content,
          description: page?.Name,
          links: [{ url: pageUrl }],
        };
      }
      case 'open-website': {
        return {
          Icon: ArrowSquareOut,
          title: item?.content,
          links: [{ url: item?.action?.data?.url }],
        };
      }
      case 'open-email': {
        return {
          Icon: EnvelopeSimple,
          title: item?.content,
          description: item?.action?.data?.sendToEmail,
          links: [{ url: getEmailUrl(item?.action?.data) }],
        };
      }
      default:
        return null;
    }
  }, [item, page]);

  if (!qrProps) return null;

  return <QRComponent {...qrProps} />;
};
